import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowDownIcon, ArrowUpIcon, PlusIcon, XIcon } from 'lucide-react';

import { ApiError, api, type QuestionRow } from '@/lib/api';
import { InfoHint } from '@/components/info-hint';
import { useT, type Interpolations, type Translate } from '@/lib/i18n';
import type { StringKey } from '@/lib/strings/en';
import { notify } from '@/lib/notify';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

/**
 * THE SECOND DATA-DRIVEN CORE, and the authoring mirror of the first.
 *
 * `features/run/renderers.tsx` holds `RENDERERS: Record<itemType, FC>` — how a
 * candidate ANSWERS each of the seven types. This holds `ITEM_TYPES:
 * Record<itemType, TypeSpec>` — how an author WRITES one. The two maps are
 * deliberately shaped the same way and are the only places in the console that
 * know an item type exists: adding an eighth type is one entry in each, and no
 * branch anywhere else.
 *
 * Until now this half did not exist. The editor covered `likert` and `slider`
 * and nothing else, so five of the seven types could be scored, delivered,
 * rendered and imported — but not authored. `plan-beta` B1 is that gap, and it
 * is the one that decides whether an authoring team can use the platform at all.
 *
 * WHERE VALIDATION ACTUALLY LIVES, and what changed (`plan-beta` E).
 *
 * The contract's `Item` discriminated union is the authority and re-parses on
 * every write at the repository boundary. The per-field rules below are the same
 * rules restated, so a message can sit beside the field it is about rather than
 * arriving in a banner after a round trip.
 *
 * **The draft is now ALSO parsed against the real `Item` schema before it is
 * sent** — `contractProblems` below. That closes the gap this comment used to
 * describe as future work: two spellings of one ruleset drift, and the mirrored
 * half was the one that could silently become more permissive. Now anything the
 * contract would reject is caught here even if the mirror missed it.
 *
 * **What was NOT done, deliberately: `zodResolver` and react-hook-form.** E names
 * them, and adopting them would mean rewriting a working seven-type form to gain
 * the property the previous paragraph already delivers — while losing the
 * `Problem[]` key design that makes these messages translatable. The value was
 * "validation cannot drift from the contract"; the form library was one route to
 * it, and not the cheap one.
 *
 * (The comment this replaced said the contract was not reachable from the client
 * build. It has been: `@contract` is aliased in both `tsconfig` and `vite.config`,
 * and the schemas are plain Zod with no server imports.)
 */

/** Guardrail G1 — no item may carry more than twelve options. */
export const G1_MAX_OPTIONS = 12;

type OptionMeta = 'isCorrect' | 'value' | 'anchorLevel' | 'dimension';

interface TypeSpec {
  /**
   * String KEYS, not text.
   *
   * The map is the console's registry of item types, and a registry that carried
   * English would be the one place a type could not be renamed for a translator
   * without editing a data structure. Typed as `StringKey`, so a type whose label
   * has no catalogue entry fails the build.
   */
  label: StringKey;
  /** What the answer IS. Decides the whole field set, exactly as it decides the renderer. */
  shape: 'scale' | 'options';
  scale?: { min: number; max: number };
  /** The per-option scoring key this type requires — the contract's `superRefine`, mirrored. */
  optionMeta?: OptionMeta;
  minOptions?: number;
  maxOptions?: number;
  hint: StringKey;
}

export const ITEM_TYPES: Record<string, TypeSpec> = {
  likert: {
    label: 'itemType.likert',
    shape: 'scale',
    scale: { min: 1, max: 5 },
    hint: 'itemType.likert.hint',
  },
  slider: {
    label: 'itemType.slider',
    shape: 'scale',
    scale: { min: 0, max: 100 },
    hint: 'itemType.slider.hint',
  },
  forced_choice: {
    label: 'itemType.forced_choice',
    shape: 'options',
    optionMeta: 'dimension',
    minOptions: 2,
    maxOptions: 3,
    hint: 'itemType.forced_choice.hint',
  },
  ranking: {
    label: 'itemType.ranking',
    shape: 'options',
    optionMeta: 'dimension',
    minOptions: 2,
    maxOptions: G1_MAX_OPTIONS,
    hint: 'itemType.ranking.hint',
  },
  mcq: {
    label: 'itemType.mcq',
    shape: 'options',
    optionMeta: 'isCorrect',
    minOptions: 2,
    maxOptions: G1_MAX_OPTIONS,
    hint: 'itemType.mcq.hint',
  },
  sjt: {
    label: 'itemType.sjt',
    shape: 'options',
    optionMeta: 'value',
    minOptions: 2,
    maxOptions: G1_MAX_OPTIONS,
    hint: 'itemType.sjt.hint',
  },
  bars: {
    label: 'itemType.bars',
    shape: 'options',
    optionMeta: 'anchorLevel',
    minOptions: 2,
    maxOptions: G1_MAX_OPTIONS,
    hint: 'itemType.bars.hint',
  },
};

export const ITEM_TYPE_IDS = Object.keys(ITEM_TYPES);

/* ------------------------------------------------------------------- draft */

export interface DraftOption {
  optionId: string;
  labelEn: string;
  labelAr: string;
  /** Held as strings so an empty field stays distinguishable from a deliberate 0. */
  value: string;
  anchorLevel: string;
  dimension: string;
  isCorrect: boolean;
}

export interface Draft {
  /** null while creating. Set means PUT, and means `status` must be carried through. */
  itemId: string | null;
  status: string;
  moduleId: string;
  itemType: string;
  promptEn: string;
  promptAr: string;
  dimension: string;
  scaleMin: number;
  scaleMax: number;
  reverseKeyed: boolean;
  anchorsEn: string;
  anchorsAr: string;
  options: DraftOption[];
  tags: string;
  skillId: string;
}

const emptyOption = (optionId: string): DraftOption => ({
  optionId, labelEn: '', labelAr: '', value: '', anchorLevel: '', dimension: '', isCorrect: false,
});

/**
 * A fresh option id.
 *
 * Never reuses a live one: an option id is the identity a stored response points
 * at, and renumbering on reorder — or handing a deleted option's id to a new one
 * — would silently re-point an answer at different text. Reordering therefore
 * moves rows and leaves ids where they are.
 *
 * (Instances are safe from this regardless, because they froze their own copy of
 * the item at generation. The rule holds anyway: the cascade-killer is a
 * backstop, not a licence.)
 */
function nextOptionId(options: DraftOption[]): string {
  const highest = options.reduce((max, o) => {
    const n = Number(/^o(\d+)$/.exec(o.optionId)?.[1] ?? 0);
    return n > max ? n : max;
  }, 0);
  return `o${highest + 1}`;
}

export function newDraft(moduleId: string): Draft {
  return {
    itemId: null,
    status: 'draft',
    moduleId,
    itemType: 'likert',
    promptEn: '',
    promptAr: '',
    dimension: '',
    scaleMin: 1,
    scaleMax: 5,
    reverseKeyed: false,
    anchorsEn: '',
    anchorsAr: '',
    options: [emptyOption('o1'), emptyOption('o2')],
    tags: '',
    skillId: '',
  };
}

/** An existing item, as the editor's draft. The inverse of `toItem`. */
export function draftFrom(item: QuestionRow): Draft {
  const anchors = (item as { anchors?: { en: string[]; ar: string[] } }).anchors;
  const base = newDraft(item.moduleId);

  return {
    ...base,
    itemId: item.itemId,
    // Carried, never reset. `PUT /questions/:id` replaces the whole document, so
    // an editor that defaulted this to 'draft' would silently un-certify every
    // item anyone corrected — the loudest possible version of a quiet bug.
    status: item.status,
    moduleId: item.moduleId,
    itemType: item.itemType,
    promptEn: item.prompt.en,
    promptAr: item.prompt.ar,
    dimension: item.dimension ?? '',
    scaleMin: item.scaleMin ?? ITEM_TYPES[item.itemType]?.scale?.min ?? 1,
    scaleMax: item.scaleMax ?? ITEM_TYPES[item.itemType]?.scale?.max ?? 5,
    reverseKeyed: item.reverseKeyed ?? false,
    anchorsEn: (anchors?.en ?? []).join(' | '),
    anchorsAr: (anchors?.ar ?? []).join(' | '),
    options: (item.options ?? []).map((o) => ({
      optionId: o.optionId,
      labelEn: o.label.en,
      labelAr: o.label.ar,
      value: o.value === undefined ? '' : String(o.value),
      anchorLevel: o.anchorLevel === undefined ? '' : String(o.anchorLevel),
      dimension: o.dimension ?? '',
      isCorrect: o.isCorrect === true,
    })),
    tags: item.meta.tags.join(', '),
    skillId: item.skillId ?? '',
  };
}

const splitAnchors = (raw: string) => raw.split('|').map((s) => s.trim()).filter(Boolean);

/**
 * The draft as the contract's `Item`.
 *
 * Projects ONLY the keys the chosen type declares. That is the point of driving
 * it from `ITEM_TYPES`: a draft that was briefly an mcq and is now a likert must
 * not post the options it used to have, and a blocklist would keep covering the
 * wrong set as the union grows.
 */
export function toItem(draft: Draft): Record<string, unknown> {
  const spec = ITEM_TYPES[draft.itemType];

  const common = {
    moduleId: draft.moduleId,
    itemType: draft.itemType,
    prompt: { en: draft.promptEn.trim(), ar: draft.promptAr.trim() },
    // Authoring never changes lifecycle state. Creating yields a draft;
    // editing keeps whatever the item already is, and the transition routes
    // are the only thing that moves it.
    status: draft.status,
    version: 1,
    locales: ['en-AE', 'ar-AE'],
    ...(draft.skillId.trim() ? { skillId: draft.skillId.trim() } : {}),
    meta: {
      tags: draft.tags.split(',').map((t) => t.trim()).filter(Boolean),
      audienceTags: [],
      sensitivityFlags: [],
      contextTags: [],
      taxonomyRefs: [],
    },
  };

  if (spec?.shape === 'scale') {
    const en = splitAnchors(draft.anchorsEn);
    const ar = splitAnchors(draft.anchorsAr);
    return {
      ...common,
      dimension: draft.dimension.trim(),
      scaleMin: draft.scaleMin,
      scaleMax: draft.scaleMax,
      reverseKeyed: draft.reverseKeyed,
      // Omitted entirely rather than sent half-filled: the contract requires two
      // of each language, and an anchor set covering one language is worse than
      // none — it renders labelled in English and bare in Arabic.
      ...(en.length >= 2 && ar.length >= 2 ? { anchors: { en, ar } } : {}),
    };
  }

  return {
    ...common,
    options: draft.options.map((o) => ({
      optionId: o.optionId,
      label: { en: o.labelEn.trim(), ar: o.labelAr.trim() },
      ...(spec?.optionMeta === 'isCorrect' ? { isCorrect: o.isCorrect } : {}),
      ...(spec?.optionMeta === 'value' && o.value !== '' ? { value: Number(o.value) } : {}),
      ...(spec?.optionMeta === 'anchorLevel' && o.anchorLevel !== ''
        ? { anchorLevel: Number(o.anchorLevel) } : {}),
      ...(spec?.optionMeta === 'dimension' && o.dimension.trim()
        ? { dimension: o.dimension.trim() } : {}),
    })),
  };
}

/**
 * A validation failure, as a key and its numbers — **not as a sentence**.
 *
 * `problems()` is a pure function with no access to a locale, and it is used by
 * tests as well as by the form. Returning English would have made it the one
 * validator in the console that could only speak one language, and would have
 * left its tests asserting on prose — so a rewording, in either language, would
 * break them for no reason connected to the rule.
 */
export interface Problem {
  key: StringKey;
  vars?: Interpolations;
}

/**
 * The contract's per-type rules, restated to name the field.
 *
 * Every rule here exists in `contract/item.ts` and is enforced there; none is
 * invented, and none is looser. Its only job is to say "sjt option 3 has no
 * value" beside option 3 instead of after a failed POST.
 */
export function problems(draft: Draft): Problem[] {
  const spec = ITEM_TYPES[draft.itemType];
  const found: Problem[] = [];

  if (!draft.promptEn.trim()) found.push({ key: 'questions.problem.promptEn' });
  if (!spec) {
    return [...found, { key: 'questions.problem.noFieldSet', vars: { type: draft.itemType } }];
  }

  if (spec.shape === 'scale') {
    if (!draft.dimension.trim()) found.push({ key: 'questions.problem.dimension' });
    if (draft.scaleMax <= draft.scaleMin) found.push({ key: 'questions.problem.scaleRange' });

    const en = splitAnchors(draft.anchorsEn);
    const ar = splitAnchors(draft.anchorsAr);
    if ((en.length > 0 || ar.length > 0) && (en.length < 2 || ar.length < 2)) {
      found.push({ key: 'questions.problem.anchorsBoth' });
    }
    return found;
  }

  const min = spec.minOptions ?? 2;
  const max = spec.maxOptions ?? G1_MAX_OPTIONS;
  // The type's NAME is itself a key, so it is resolved by the renderer — which is
  // why `vars` carries the key rather than a resolved label.
  if (draft.options.length < min) {
    found.push({ key: 'questions.problem.minOptions', vars: { type: spec.label, min } });
  }
  if (draft.options.length > max) {
    found.push({ key: 'questions.problem.maxOptions', vars: { type: spec.label, max } });
  }

  draft.options.forEach((o, i) => {
    const n = i + 1;
    if (!o.labelEn.trim()) found.push({ key: 'questions.problem.optionLabel', vars: { n } });
    if (spec.optionMeta === 'value' && o.value === '') {
      found.push({ key: 'questions.problem.optionValue', vars: { n } });
    }
    if (spec.optionMeta === 'anchorLevel' && o.anchorLevel === '') {
      found.push({ key: 'questions.problem.optionAnchor', vars: { n } });
    }
    if (spec.optionMeta === 'dimension' && !o.dimension.trim()) {
      found.push({ key: 'questions.problem.optionDimension', vars: { n } });
    }
  });

  if (spec.optionMeta === 'isCorrect' && draft.options.filter((o) => o.isCorrect).length !== 1) {
    found.push({ key: 'questions.problem.oneCorrect' });
  }

  return found;
}

/** Render a problem, resolving the type-name key `minOptions`/`maxOptions` carry. */
export function problemText(problem: Problem, t: Translate): string {
  const vars = problem.vars?.type
    ? { ...problem.vars, type: t(problem.vars.type as StringKey) }
    : problem.vars;
  return t(problem.key, vars);
}

/* ---------------------------------------------------------------- options */

function OptionsEditor({
  spec, options, onChange,
}: {
  spec: TypeSpec;
  options: DraftOption[];
  onChange: (next: DraftOption[]) => void;
}) {
  const t = useT();
  const max = spec.maxOptions ?? G1_MAX_OPTIONS;
  const min = spec.minOptions ?? 2;

  const patch = (index: number, part: Partial<DraftOption>) =>
    onChange(options.map((o, i) => (i === index ? { ...o, ...part } : o)));

  /** Exclusive by construction — the contract allows exactly one correct mcq option. */
  const setCorrect = (index: number) =>
    onChange(options.map((o, i) => ({ ...o, isCorrect: i === index })));

  const move = (index: number, by: number) => {
    const to = index + by;
    if (to < 0 || to >= options.length) return;
    const next = [...options];
    [next[index], next[to]] = [next[to]!, next[index]!];
    onChange(next);
  };

  return (
    <div className="grid gap-3">
      <div className="flex items-center justify-between">
        <Label>{t('questions.options')}</Label>
        <span className="text-muted-foreground text-xs">
          {t('questions.optionsOf', { count: options.length, max })}
        </span>
      </div>

      {options.map((option, index) => (
        <div key={option.optionId} className="grid gap-2 rounded-md border p-3">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground font-mono text-xs" dir="ltr">
              {option.optionId}
            </span>
            <div className="ms-auto flex gap-1">
              <Button
                type="button" size="icon" variant="ghost" className="size-7"
                aria-label={t('questions.option.moveUp', { n: index + 1 })}
                disabled={index === 0}
                onClick={() => move(index, -1)}
              >
                <ArrowUpIcon className="size-3.5" />
              </Button>
              <Button
                type="button" size="icon" variant="ghost" className="size-7"
                aria-label={t('questions.option.moveDown', { n: index + 1 })}
                disabled={index === options.length - 1}
                onClick={() => move(index, 1)}
              >
                <ArrowDownIcon className="size-3.5" />
              </Button>
              <Button
                type="button" size="icon" variant="ghost" className="size-7"
                aria-label={t('questions.option.remove', { n: index + 1 })}
                disabled={options.length <= min}
                onClick={() => onChange(options.filter((_, i) => i !== index))}
              >
                <XIcon className="size-3.5" />
              </Button>
            </div>
          </div>

          {/* BOTH languages stay side by side whatever the operator reads. A
              certified item is required by the contract to carry both, so an
              editor that showed only the operator's language would produce items
              that cannot be certified — this is authoring, not display. */}
          <div className="grid gap-2 sm:grid-cols-2">
            <Input
              dir="ltr"
              aria-label={t('questions.option.labelEn', { n: index + 1 })}
              placeholder={t('questions.label.en')}
              value={option.labelEn}
              onChange={(e) => patch(index, { labelEn: e.target.value })}
            />
            <Input
              dir="rtl"
              aria-label={t('questions.option.labelAr', { n: index + 1 })}
              placeholder={t('questions.label.ar')}
              value={option.labelAr}
              onChange={(e) => patch(index, { labelAr: e.target.value })}
            />
          </div>

          {/* The one field that differs between the five choice types. */}
          {spec.optionMeta === 'isCorrect' && (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="correct-option"
                aria-label={t('questions.option.isCorrect', { n: index + 1 })}
                checked={option.isCorrect}
                onChange={() => setCorrect(index)}
              />
              {t('questions.option.correctAnswer')}
            </label>
          )}
          {spec.optionMeta === 'value' && (
            <Input
              type="number"
              aria-label={t('questions.option.valueLabel', { n: index + 1 })}
              placeholder={t('questions.option.value')}
              value={option.value}
              onChange={(e) => patch(index, { value: e.target.value })}
            />
          )}
          {spec.optionMeta === 'anchorLevel' && (
            <Input
              type="number" min={1}
              aria-label={t('questions.option.anchorLabel', { n: index + 1 })}
              placeholder={t('questions.option.anchor')}
              value={option.anchorLevel}
              onChange={(e) => patch(index, { anchorLevel: e.target.value })}
            />
          )}
          {spec.optionMeta === 'dimension' && (
            <Input
              aria-label={t('questions.option.dimensionLabel', { n: index + 1 })}
              placeholder={t('questions.option.dimension')}
              value={option.dimension}
              onChange={(e) => patch(index, { dimension: e.target.value })}
            />
          )}
        </div>
      ))}

      <Button
        type="button" variant="outline" size="sm"
        disabled={options.length >= max}
        onClick={() => onChange([...options, emptyOption(nextOptionId(options))])}
      >
        <PlusIcon className="size-4" />
        {t('questions.option.add')}
      </Button>
    </div>
  );
}

/* ------------------------------------------------- where this item fits (B6) */

/**
 * Which matrix cells this item qualifies for, shown beside the tags that decide
 * it (`plan-beta` B6).
 *
 * The loop B6 calls invisible: `audienceTags` and `contextTags` are what a
 * cell's `pool.query` filters on, so setting one either puts the item into a
 * sampling pool or does nothing — and until now an author could not tell which.
 *
 * Only for an item that EXISTS. A draft being typed has no id to ask about, and
 * a panel that said "0 cells" while someone was still choosing tags would be
 * answering a question they had not finished asking.
 */
function QualifyingCells({ itemId }: { itemId: string }) {
  const t = useT();
  const cells = useQuery({
    queryKey: ['item-cells', itemId],
    queryFn: () => api.itemCells(itemId),
  });

  // Defensive on the array as well as the object: this is a hint panel beside a
  // form, and an unexpected payload must not take the editor down with it.
  const fits = cells.data?.cells;
  if (cells.isLoading || !Array.isArray(fits)) return null;
  const certified = cells.data?.certified ?? false;

  return (
    <div className="rounded-md border p-3">
      <p className="mb-1 text-sm font-medium">
        {t('questions.cells.title')}
        <InfoHint>{t('questions.cells.detail')}</InfoHint>
      </p>

      {fits.length === 0 ? (
        <p className="text-muted-foreground text-xs">{t('questions.cells.none')}</p>
      ) : (
        <div className="flex flex-wrap gap-1">
          {fits.map((cell) => (
            <Badge key={cell.cellId} variant={cell.pinned ? 'default' : 'secondary'}>
              {cell.personaId}
              {cell.pinned && ` · ${t('questions.cells.pinned')}`}
            </Badge>
          ))}
        </div>
      )}

      {/* The status caveat, because "qualifies" and "will be drawn" differ by
          one lifecycle transition and an author reading this is usually mid-way
          through making them the same thing. */}
      {!certified && fits.length > 0 && (
        <p className="text-muted-foreground mt-2 text-xs">{t('questions.cells.notCertified')}</p>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------- editor */

export function ItemEditor({
  moduleIds, initial, onSaved,
}: {
  moduleIds: string[];
  /** An existing item to correct, or undefined to author a new one. */
  initial?: QuestionRow;
  onSaved: () => void;
}) {
  const t = useT();
  const [draft, setDraft] = useState<Draft>(() =>
    initial ? draftFrom(initial) : newDraft(moduleIds[0] ?? 'behavioural'));
  const [error, setError] = useState<string | null>(null);
  const [showProblems, setShowProblems] = useState(false);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  /**
   * Switching type re-seeds the fields that type owns, and only those. A likert
   * that becomes a slider keeps its dimension and prompt — the same question on
   * a different scale — but takes the new scale's range, because 1–5 on a slider
   * is a control with five reachable positions out of a hundred.
   */
  const setType = (itemType: string) =>
    setDraft((d) => {
      const spec = ITEM_TYPES[itemType];
      return {
        ...d,
        itemType,
        scaleMin: spec?.scale?.min ?? d.scaleMin,
        scaleMax: spec?.scale?.max ?? d.scaleMax,
        options: d.options.length >= 2 ? d.options : [emptyOption('o1'), emptyOption('o2')],
      };
    });

  const spec = ITEM_TYPES[draft.itemType];
  const found = problems(draft);
  const editing = draft.itemId !== null;

  const save = useMutation({
    mutationFn: () => (editing
      ? api.updateQuestion(draft.itemId!, toItem(draft))
      : api.createQuestion(toItem(draft))),
    onSuccess: (item) => {
      notify.success(
        t(editing ? 'questions.saved' : 'questions.created'),
        t(editing ? 'questions.saved.detail' : 'questions.created.detail', { item: item.itemId }),
      );
      setError(null);
      onSaved();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : t('questions.saveFailed')),
  });

  const submit = () => {
    if (found.length > 0) {
      setShowProblems(true);
      return;
    }
    save.mutate();
  };

  return (
    <DialogContent className="max-h-[90vh] sm:max-w-2xl overflow-y-auto">
      <DialogHeader>
        <DialogTitle>
          {editing ? t('questions.editTitle', { item: draft.itemId ?? '' }) : t('questions.newItem')}
        </DialogTitle>
        <DialogDescription>
          {t(editing ? 'questions.editHint' : 'questions.newHint')}
        </DialogDescription>
      </DialogHeader>

      <div className="grid gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor="module">{t('questions.column.module')}</Label>
            <Select value={draft.moduleId} onValueChange={(v) => set('moduleId', v)}>
              <SelectTrigger id="module"><SelectValue /></SelectTrigger>
              <SelectContent>
                {moduleIds.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="itemType">{t('questions.column.type')}</Label>
            <Select value={draft.itemType} onValueChange={setType}>
              <SelectTrigger id="itemType"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ITEM_TYPE_IDS.map((id) => (
                  <SelectItem key={id} value={id}>{t(ITEM_TYPES[id]!.label)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {spec && <p className="text-muted-foreground -mt-2 text-xs">{t(spec.hint)}</p>}

        {/* Both languages, side by side. A certified item must carry both — so
            these two fields are LABELLED by language rather than following the
            operator's, and each input carries its own `dir`. */}
        <div className="grid gap-2">
          <Label htmlFor="promptEn">{t('questions.prompt.en')}</Label>
          <Input
            id="promptEn" dir="ltr" value={draft.promptEn}
            onChange={(e) => set('promptEn', e.target.value)}
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="promptAr">{t('questions.prompt.ar')}</Label>
          <Input id="promptAr" dir="rtl" value={draft.promptAr} onChange={(e) => set('promptAr', e.target.value)} />
        </div>

        {spec?.shape === 'scale' && (
          <>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="grid gap-2">
                <Label htmlFor="dimension">{t('questions.dimension')}</Label>
                <Input
                  id="dimension"
                  dir="ltr"
                  value={draft.dimension}
                  onChange={(e) => set('dimension', e.target.value)}
                  // A dimension KEY, not a display name — it must match the
                  // scoring config, so it is not translated.
                  placeholder="conscientiousness"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="scaleMin">{t('questions.scaleMin')}</Label>
                <Input
                  id="scaleMin" type="number" value={draft.scaleMin}
                  onChange={(e) => set('scaleMin', Number(e.target.value))}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="scaleMax">{t('questions.scaleMax')}</Label>
                <Input
                  id="scaleMax" type="number" value={draft.scaleMax}
                  onChange={(e) => set('scaleMax', Number(e.target.value))}
                />
              </div>
            </div>

            {/*
              Anchors are what the candidate reads to know what "4" means — not
              scoring metadata, which is why they are the one authored field that
              crosses into delivery. Separated by | so an anchor may contain a
              comma, which several of the seeded ones do.
            */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="anchorsEn">{t('questions.anchors.en')}</Label>
                <Input
                  id="anchorsEn" dir="ltr" value={draft.anchorsEn}
                  placeholder={t('questions.anchors.placeholder')}
                  onChange={(e) => set('anchorsEn', e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="anchorsAr">{t('questions.anchors.ar')}</Label>
                <Input
                  id="anchorsAr" dir="rtl" value={draft.anchorsAr}
                  onChange={(e) => set('anchorsAr', e.target.value)}
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                id="reverseKeyed" type="checkbox" checked={draft.reverseKeyed}
                onChange={(e) => set('reverseKeyed', e.target.checked)}
              />
              <Label htmlFor="reverseKeyed" className="font-normal">
                {t('questions.reverseKeyed')}
              </Label>
            </div>
          </>
        )}

        {spec?.shape === 'options' && (
          <OptionsEditor spec={spec} options={draft.options} onChange={(o) => set('options', o)} />
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor="tags">{t('questions.tags')}</Label>
            <Input id="tags" value={draft.tags} onChange={(e) => set('tags', e.target.value)} />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="skillId">{t('questions.skillId')}</Label>
            <Input
              id="skillId" dir="ltr" value={draft.skillId}
              onChange={(e) => set('skillId', e.target.value)}
            />
          </div>
        </div>

        {editing && draft.itemId && <QualifyingCells itemId={draft.itemId} />}

        {showProblems && found.length > 0 && (
          <Alert variant="destructive" role="alert">
            <AlertDescription>
              <ul className="list-disc ps-4">
                {found.map((p) => (
                  <li key={`${p.key}:${JSON.stringify(p.vars ?? {})}`}>{problemText(p, t)}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </div>

      <DialogFooter>
        <Button onClick={submit} disabled={save.isPending}>
          {save.isPending
            ? t('common.saving')
            : editing ? t('common.save') : t('questions.createDraft')}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
